import Analytics, { AnalyticsInstance } from 'analytics'

import { AnalyticsServiceSettings } from 'models/AnalyticsServiceSettings'
import { RootState, TypedDispatch } from 'redux/store'
import { makeFacebookPixelAnalyticsPlugin } from './plugins/facebook-pixel'
import { makeFacebookCapiAnalyticsPlugin } from './plugins/facebook-capi'
import heap from './plugins/heap'
import { makeCrossFrameAnalyticsPlugin } from './plugins/cross-frame-analytics'
import { isBot } from '../../utils/functions/isBot'
import Bugsnag from '@bugsnag/js'
import { getClassySessionId } from 'modules/utils/getClassySessionId'

let analyticsInstance: AnalyticsInstance | null = null

// Define an interface for events that will be queued when analytics is not initialized
interface QueuedEvent {
  method: 'track' | 'page' | 'once'
  args: unknown[]
  eventName?: string
  callback?: (...params: unknown[]) => unknown
}

// Initialize an array to hold the queued events
const eventQueue: QueuedEvent[] = []

/**
 * Utility function to flatten nested objects into a flat structure
 * with keys using underscores instead of dot notation.
 */
const flattenObject = (
  obj: Record<string, unknown>,
  parentKey = '',
  sep = '_',
): Record<string, unknown> => {
  const flattened: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(obj)) {
    const newKey = parentKey ? `${parentKey}${sep}${key}` : key

    if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
      Object.assign(flattened, flattenObject(value as Record<string, unknown>, newKey, sep))
    } else {
      flattened[newKey] = value
    }
  }

  return flattened
}

/**
 * Resets the analytics instance and clears the event queue.
 *
 * This is generally only used for resetting the analytics instance in tests.
 */
export const resetAnalyticsInstance = (): void => {
  analyticsInstance = null
  eventQueue.length = 0
}

/**
 * Process and send events that were queued when analytics was not initialized
 */
const processEventsQueue = () => {
  if (analyticsInstance) {
    while (eventQueue.length > 0) {
      const event = eventQueue.shift()

      if (!event) {
        return
      }

      const { method, args, eventName, callback } = event

      if (method === 'once' && eventName && callback) {
        analyticsInstance.once(eventName, callback)
      } else {
        // @ts-expect-error | Typescript doesn't like spreading args into a function
        analyticsInstance[method](...args)
      }
    }
  }
}

interface AnalyticsInstanceProps {
  campaignId: string
  organizationId: string
  cartId?: string
  dispatch: TypedDispatch<RootState>
  settings: AnalyticsServiceSettings[]
}

/**
 * Initializes the Analytics instance with the correct settings and plugins.
 */
export const initializeAnalytics = ({
  campaignId,
  organizationId,
  dispatch,
  settings,
}: AnalyticsInstanceProps): void => {
  try {
    /**
     * Do not initialize analytics if the instance has already been initialized.
     */
    if (analyticsInstance) {
      console.warn('Analytics instance has already been initialized.')
      return
    }

    /**
     * Do not initialize analytics for bot traffic.
     */
    if (isBot()) {
      console.warn('Analytics not initialized for bot traffic.')
      return
    }

    const plugins = []

    /**
     * Facebook Pixel Plugin
     */
    const facebookPixelPlugin = makeFacebookPixelAnalyticsPlugin(settings)
    facebookPixelPlugin && plugins.push(facebookPixelPlugin)

    /**
     * Facebook CAPI Plugin
     */
    const facebookCapiPlugin = makeFacebookCapiAnalyticsPlugin({
      dispatch,
      settings,
      organizationId,
    })

    facebookCapiPlugin && plugins.push(facebookCapiPlugin)

    /**
     * Heap Plugin
     */
    plugins.push(
      heap({
        campaignId: campaignId,
        orgId: organizationId,
        environment: process.env.APP_ENV ?? 'dev',
      }),
    )

    /**
     * Cross Frame Analytics Plugin
     */
    const crossFramePlugin = makeCrossFrameAnalyticsPlugin({ settings })
    crossFramePlugin && plugins.push(crossFramePlugin)

    // Initialize the Analytics instance
    analyticsInstance = Analytics({
      app: 'Classy Embedded Giving',
      plugins,
    })

    /**
     * Wrap analytics `track` method to ensure event data is flattened before being sent.
     */
    const originalTrack = analyticsInstance.track

    analyticsInstance.track = (
      eventName: string,
      payload?: Record<string, unknown>,
      options?: unknown,
      callback?: (...params: unknown[]) => unknown,
    ): Promise<unknown> => {
      // Flatten the payload (event data) if it exists
      const flattenedPayload = payload ? flattenObject(payload) : {}

      // Call the original track method with all parameters and return its promise
      return originalTrack.call(analyticsInstance, eventName, flattenedPayload, options, callback)
    }

    /**
     * Process and send events that were queued when analyticsInstance was not initialized
     */
    processEventsQueue()
    console.log('Analytics initialized successfully.')
  } catch (error) {
    console.error('Error initializing analytics:', error)

    Bugsnag.notify(new Error('Error initializing analytics:'), (event) => {
      event.severity = 'info'
      event.setUser(getClassySessionId())
    })
  }
}

/**
 * Returns the initialized Analytics.js instance or queues events if not initialized.
 */
export const getAnalyticsInstance = (): AnalyticsInstance => {
  if (!analyticsInstance) {
    console.warn('Analytics instance has not been initialized.')

    // Return a shim for queueing events until analyticsInstance is initialized
    return {
      track: (...args: unknown[]) => eventQueue.push({ method: 'track', args }),
      page: (...args: unknown[]) => eventQueue.push({ method: 'page', args }),
      once: (eventName: string, callback?: (...params: unknown[]) => unknown) => {
        eventQueue.push({ method: 'once', args: [], eventName, callback })

        // eslint-disable-next-line @typescript-eslint/no-empty-function
        return () => {}
      },
      eventQueue,
    } as unknown as AnalyticsInstance // Cast to AnalyticsInstance
  }

  return analyticsInstance
}
